import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Group, Stack, Text, Title, HoverCard, UnstyledButton } from '@mantine/core';
import { Pie } from '@visx/shape';
import { Group as VisxGroup } from '@visx/group';
import { ParentSize } from '@visx/responsive';
import { scaleOrdinal } from '@visx/scale';
import { SportMixEntry } from '../../data/PingSummary';

type Props = {
    data: SportMixEntry[] | undefined;
};

/** Charts keep full colour — categories need hue to stay separable. */
const PALETTE = ['#4dabf7', '#f06595', '#9775fa', '#51cf66', '#ffd43b', '#ff922b', '#22b8cf', '#fa5252'];

const SportMixDonut: React.FC<Props> = ({ data }) => {
    const navigate = useNavigate();
    const entries = data ?? [];
    const total = entries.reduce((sum, e) => sum + e.count, 0);
    const [hoveredSport, setHoveredSport] = useState<string | null>(null);

    const colorScale = scaleOrdinal<string, string>({
        domain: entries.map((e) => e.sport),
        range: PALETTE,
    });

    return (
        <Card shadow="xs" withBorder padding="md" h="100%">
            <Stack gap="xs" h="100%">
                <Title order={5}>Sport mix today</Title>
                {entries.length === 0 ? (
                    <Text c="dimmed" size="sm">
                        No sports recorded yet today.
                    </Text>
                ) : (
                    <Group align="flex-start" wrap="nowrap" gap="lg" style={{ flex: 1 }}>
                        <div style={{ width: 180, height: 180, flexShrink: 0 }}>
                            <ParentSize>
                                {({ width, height }) => {
                                    const size = Math.min(width, height);
                                    const radius = size / 2;
                                    return (
                                        <svg width={size} height={size}>
                                            <VisxGroup top={radius} left={radius}>
                                                <Pie
                                                    data={entries}
                                                    pieValue={(d) => d.count}
                                                    outerRadius={radius}
                                                    innerRadius={radius * 0.6}
                                                    padAngle={0.01}
                                                >
                                                    {(pie) =>
                                                        pie.arcs.map((arc) => {
                                                            const path = pie.path(arc) ?? '';
                                                            const isDimmed =
                                                                hoveredSport !== null &&
                                                                hoveredSport !== arc.data.sport;
                                                            return (
                                                                <path
                                                                    key={arc.data.sport}
                                                                    d={path}
                                                                    fill={colorScale(arc.data.sport)}
                                                                    opacity={isDimmed ? 0.3 : 1}
                                                                    style={{ transition: 'opacity 120ms ease' }}
                                                                />
                                                            );
                                                        })
                                                    }
                                                </Pie>
                                                <text
                                                    textAnchor="middle"
                                                    dy="0.35em"
                                                    fontSize={20}
                                                    fontWeight={700}
                                                    fill="currentColor"
                                                >
                                                    {total}
                                                </text>
                                            </VisxGroup>
                                        </svg>
                                    );
                                }}
                            </ParentSize>
                        </div>
                        <Stack gap={6} style={{ flex: 1, minWidth: 0 }}>
                            {entries.map((entry) => {
                                const pct = total > 0 ? Math.round((entry.count / total) * 100) : 0;
                                return (
                                    <HoverCard
                                        key={entry.sport}
                                        width={260}
                                        shadow="md"
                                        openDelay={120}
                                        closeDelay={80}
                                        position="right"
                                        withinPortal
                                    >
                                        <HoverCard.Target>
                                            <Group
                                                gap="xs"
                                                wrap="nowrap"
                                                onMouseEnter={() => setHoveredSport(entry.sport)}
                                                onMouseLeave={() => setHoveredSport(null)}
                                                style={{
                                                    cursor: 'default',
                                                    padding: '2px 4px',
                                                    borderRadius: 4,
                                                    background:
                                                        hoveredSport === entry.sport
                                                            ? 'var(--mantine-color-default-hover)'
                                                            : 'transparent',
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        width: 10,
                                                        height: 10,
                                                        borderRadius: 2,
                                                        backgroundColor: colorScale(entry.sport),
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                <Text size="sm" style={{ flex: 1, minWidth: 0 }} truncate>
                                                    {entry.sport}
                                                </Text>
                                                <Text size="sm" c="dimmed">
                                                    {pct}%
                                                </Text>
                                            </Group>
                                        </HoverCard.Target>
                                        <HoverCard.Dropdown>
                                            <Stack gap={4}>
                                                <Text size="xs" c="dimmed" fw={500} tt="uppercase">
                                                    {entry.sport} · {entry.count} ping
                                                    {entry.count !== 1 ? 's' : ''}
                                                </Text>
                                                {entry.devices.length === 0 ? (
                                                    <Text size="sm" c="dimmed">
                                                        No device breakdown available.
                                                    </Text>
                                                ) : (
                                                    entry.devices.map((d) => (
                                                        <UnstyledButton
                                                            key={d.id}
                                                            onClick={() => navigate(`/device/${d.id}`)}
                                                            style={{
                                                                padding: '4px 6px',
                                                                borderRadius: 4,
                                                            }}
                                                            className="hover-row"
                                                        >
                                                            <Group gap="xs" wrap="nowrap" justify="space-between">
                                                                <Text size="sm" truncate>
                                                                    {d.name}
                                                                </Text>
                                                                <Text size="xs" c="dimmed">
                                                                    {d.count}
                                                                </Text>
                                                            </Group>
                                                        </UnstyledButton>
                                                    ))
                                                )}
                                            </Stack>
                                        </HoverCard.Dropdown>
                                    </HoverCard>
                                );
                            })}
                        </Stack>
                    </Group>
                )}
            </Stack>
        </Card>
    );
};

export default SportMixDonut;
